import { filter } from 'lodash';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { getActivities } from 'src/redux/slices/activity';
import { getPracticeId } from 'src/redux/slices/clincheck';
import { consultationAttended, CompleteTreatment, FailedToAttendAppointment, followUpSlice, getLeadReminderData, StartingTreatment, startLoadingDepositPending, UpdateDepositPendingStatus, updateLeadTaskCounter } from 'src/redux/slices/lead';
import { setLoader } from 'src/redux/slices/loader';
import { getLeadTreatmentsList, setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import { dispatch } from 'src/redux/store';
import { datePickerTextFieldStyle } from 'src/styles/NewLeadTableRowStyle';

export const getAttendedActiveClass = (data) => {
    if (data === 'CONSULTATION_ATTENDED') {
        return 'activeBefore';
    }
    if (data === 'TREATMENT_STARTED' || data === 'CLOSED') {
        return 'active';
    }
    return '';
};

export const BoxActive = (data) => {
    if (data === 'TREATMENT_STARTED' ||
        data === 'CONSULTATION_ATTENDED' ||
        data === 'CONSULTATION_BOOKED' ||
        data === 'CLOSED' ||
        data === 'FAILED_TO_ATTEND_CONSULTATION'
    ) {
        return true;
    }
    return false;
};

export const attendedTreatmentAPI = (row, handleStatusUpdate) => {
    // The shared context owns appointment selection. One confirmation must not
    // attend both the nearest future booking and a separate past booking.
    dispatch(consultationAttended(getPracticeId(), row?.lead, row?.id, handleStatusUpdate, 'TreatmentAccordion'));
};

export const failedAttendTreatmentAPI = (row, handleStatusUpdate) => {
    const practiceId = getPracticeId();
    const data = row?.appointments;
    const now = moment().format();
    const Type = 'BOOKED';
    const filterData = data?.filter((value) => value.status === Type);
    const result = filter(filterData, (obj) => moment(obj.datetime, 'DD-MM-YYYY HH:mm:ss').format() > now);
    result.sort((a, b) =>
        moment(a.datetime, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ').diff(moment(b.datetime, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ'))
    );
    const result2 = filter(filterData, (obj) => moment(obj.datetime, 'DD-MM-YYYY HH:mm:ss').format() < now);
    result2.sort((a, b) =>
        moment(b.datetime, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ').diff(moment(a.datetime, 'DD-MM-YYYY HH:mm:ss.SSSSSSZ'))
    );
    // Prefer the next upcoming booking; otherwise the most recent past one.
    // Exactly ONE appointment is marked. Before 30 Oct 2026 the past branch
    // posted `result[0]?.id` (the EMPTY future list → `/appointments/undefined/
    // failed_to_attend/`), so "Failed to attend" on a past-dated booking did
    // nothing while the UI reported success (SMOKE-32 on-mode, staging 23375),
    // and a lead with both a future and a past booking posted the same id twice.
    const [target] = result?.length > 0 ? result : (result2 || []);
    if (!target?.id) return;
    const appointmentDetail = {
        contacted_via: target.contacted_via || '',
        datetime: target.datetime,
        method: target.method,
        note: target.note,
    };
    dispatch(FailedToAttendAppointment(appointmentDetail, row?.lead, target.id, practiceId, handleStatusUpdate, 'failedAttendTreatment'));
};

export const nearestData = (row) => {
    const data = row?.appointments;
    const Type = 'BOOKED';
    const filterData = data?.filter((value) => value.status === Type);
    return filterData?.length > 0 && filterData;
};

export const completeTreatmentAPI = (data, handleUpdate) => {
    const practiceId = getPracticeId();
    const row = data;
    const details = {
        treatment: row.treatment || '',
        custom_price: row.price,
    };
    dispatch(setLoader(true));
    dispatch(CompleteTreatment(details, row.lead, row.id, practiceId, handleUpdate));
};

export const startTreatmentAPI = (data, handleUpdate) => {
    const practiceId = getPracticeId();
    const row = data;
    const details = {
        treatment: row.treatment || '',
        custom_price: row.price,
        lead: row.lead,
        id: row.id,
    };
    dispatch(setLoader(true));
    dispatch(StartingTreatment(details, practiceId, handleUpdate, 'startingTreatmentFinish'));
};

export const handleMessage = ({ message, success }) => {
    enqueueSnackbar(message, {
        variant: success ? 'success' : 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' }
    });
};

export const slotProps = {
    textField: {
        fullWidth: true,
        margin: 'normal',
        sx: datePickerTextFieldStyle,
        onKeyDown: (e) => e.preventDefault()
    },
};

export const handleAction = ({ actionType, setConfirmation, setBacktoAttendConfirmation, setAttendedConfirmation, setFailedAttendConfirmation, setRevertToFTAConfirmation, setRevertToBookedConfirmation }) => {
    switch (actionType) {
        case 'complete':
            setConfirmation(true);
            break;
        case 'backToAttend':
            setBacktoAttendConfirmation(true);
            break;
        case 'attended':
            setAttendedConfirmation(true);
            break;
        case 'failedAttend':
            setFailedAttendConfirmation(true);
            break;
        case 'revertToFTA':
            if (setRevertToFTAConfirmation) setRevertToFTAConfirmation(true);
            break;
        case 'revertToBooked':
            if (setRevertToBookedConfirmation) setRevertToBookedConfirmation(true);
            break;
        default:
            break;
    }
};

export const handleDepositPendingChange = (e, { data, setDepositPaidStatus, handleUpdate }) => {
    setDepositPaidStatus(e.target.checked);
    const treatment = data?.treatment;
    const id = data?.id;
    const details = {
        leadId: data.lead,
        treatmentId: id,
        payload: {
            treatment,
            is_treatment_fee_pending: !e.target.checked,
        },
    };
    dispatch(startLoadingDepositPending(true));
    dispatch(UpdateDepositPendingStatus(details, handleUpdate));
};

export const handleSuccessReload = ({leadDetail}) => {
    const practiceId = getPracticeId();
    dispatch(updateLeadTaskCounter([]));
    dispatch(getLeadReminderData(practiceId, 'LeadDetail', 10, 0, '', leadDetail?.id, ''));
    dispatch(getLeadReminderData(practiceId, 'LeadDetail', 1, 0, '', leadDetail?.id, '', '', 'Graterthan'));
    dispatch(getActivities(practiceId, leadDetail.id, 10, 0));
    dispatch(followUpSlice(leadDetail.id, practiceId, 'LeadDetail'));
    dispatch(getLeadTreatmentsList({ leadId: leadDetail.id }));
    dispatch(setCurrentTreatmentDataGlobal(null));
};